import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPractitionersFromApi } from '@/lib/api';
import { mapPractitionerRow, TIER_ORDER } from '@/lib/practitioner';
import type { Practitioner } from '@/mocks/practitioners';

function AvailabilityBadge({ available }: { available: boolean }) {
  if (!available) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-[0.65rem] font-semibold">
      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-soft-pulse" />
      Disponible
    </span>
  );
}

function RatingBadge({ rating, reviews }: { rating?: number; reviews?: number }) {
  if (rating == null || rating <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[0.7rem] font-semibold">
      <i className="ri-star-fill text-accent-500 w-3.5 h-3.5 flex items-center justify-center" />
      {rating.toFixed(1)}
      <span className="font-normal text-accent-700/80">({reviews ?? 0} avis)</span>
    </span>
  );
}

function TopRatedBadge({ rating }: { rating?: number }) {
  if (rating == null || rating < 4.8) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[0.65rem] font-semibold">
      <i className="ri-medal-line w-3.5 h-3.5 flex items-center justify-center" />
      Très bien noté
    </span>
  );
}

function BookButton() {
  return (
    <Link
      to="/recherche"
      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className="ri-calendar-check-line w-3.5 h-3.5 flex items-center justify-center" />
      Prendre RDV
    </Link>
  );
}

function FeatureCard({ practitioner: p, large }: { practitioner: Practitioner; large?: boolean }) {
  // Même accent discret que la page Recherche : léger liseré ambré + voile doré
  // pour valoriser les Premium, sans jamais afficher leur forfait.
  const isPremium = p.subscriptionTier === 'premium';

  if (large) {
    return (
      <article
        className={`relative overflow-hidden flex flex-col sm:flex-row gap-5 p-6 rounded-2xl border transition-colors ${
          isPremium
            ? 'bg-gradient-to-br from-accent-50/70 via-background-50 to-background-50 border-accent-200'
            : 'bg-background-50 border-background-200'
        }`}
      >
        {isPremium && (
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-accent-400/70" aria-hidden="true" />
        )}
        <div className={`shrink-0 w-16 h-16 flex items-center justify-center rounded-2xl ${p.color}`}>
          <span className="font-heading font-bold text-xl">{p.initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1.5">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">{p.name}</h3>
            <AvailabilityBadge available={p.isAvailableNow} />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-0.5">
            <p className="text-sm text-primary-700 font-medium">{p.type}</p>
            <RatingBadge rating={p.rating} reviews={p.reviews} />
            <TopRatedBadge rating={p.rating} />
          </div>
          <p className="text-xs text-foreground-600 flex items-center gap-1.5 mt-1.5">
            <i className="ri-map-pin-line text-foreground-400 w-3.5 h-3.5 flex items-center justify-center" />
            {p.district}
          </p>

          <div className="flex flex-wrap gap-1.5 mt-3">
            {p.tags.map((t) => (
              <span
                key={t}
                className="px-2 py-1 rounded-full bg-background-100 text-foreground-700 text-[0.65rem] font-medium"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-3 mt-4 pt-4 border-t border-background-200">
            <div>
              <p className="text-xs text-foreground-600 flex items-center gap-1.5">
                <i className="ri-calendar-line text-foreground-400 w-3.5 h-3.5 flex items-center justify-center" />
                {p.nextSlot}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-foreground-800">
                {p.price > 0 ? (
                  <>dès <strong>{p.price} MAD</strong></>
                ) : (
                  p.category === 'Pharmacies' ? 'Tarifs en pharmacie' : 'Tarif sur demande'
                )}
              </span>
              <BookButton />
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className={`relative overflow-hidden flex flex-col p-5 rounded-2xl border transition-colors ${
        isPremium
          ? 'bg-gradient-to-br from-accent-50/70 via-background-50 to-background-50 border-accent-200 hover:border-accent-300'
          : 'bg-background-50 border-background-200 hover:border-accent-300'
      }`}
    >
      {isPremium && (
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-accent-400/70" aria-hidden="true" />
      )}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${p.color}`}>
          <span className="font-heading font-bold text-lg">{p.initials}</span>
        </div>
        <AvailabilityBadge available={p.isAvailableNow} />
      </div>

      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">{p.name}</h3>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <p className="text-sm text-primary-700 font-medium">{p.type}</p>
        <RatingBadge rating={p.rating} reviews={p.reviews} />
        <TopRatedBadge rating={p.rating} />
      </div>
      <p className="text-xs text-foreground-600 flex items-center gap-1.5 mb-4">
        <i className="ri-map-pin-line text-foreground-400 w-3.5 h-3.5 flex items-center justify-center" />
        {p.district}
      </p>

      <p className="text-xs text-foreground-600 flex items-center gap-1.5 mb-5">
        <i className="ri-calendar-line text-foreground-400 w-3.5 h-3.5 flex items-center justify-center" />
        {p.nextSlot}
      </p>

      <div className="flex items-center justify-between mt-auto pt-4 border-t border-background-200">
        <span className="text-sm text-foreground-800">
          {p.price > 0 ? (
            <>dès <strong>{p.price} MAD</strong></>
          ) : (
            p.category === 'Pharmacies' ? 'Tarifs en pharmacie' : 'Tarif sur demande'
          )}
        </span>
        <BookButton />
      </div>
    </article>
  );
}

export default function FeaturedPractitioners() {
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchFeatured = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const rows = await fetchPractitionersFromApi();

      const sorted = rows.map(mapPractitionerRow).sort((a, b) => {
        const ta = TIER_ORDER[a.subscriptionTier ?? ''] ?? 3;
        const tb = TIER_ORDER[b.subscriptionTier ?? ''] ?? 3;
        if (ta !== tb) return ta - tb;
        if (a.isAvailableNow !== b.isAvailableNow) return a.isAvailableNow ? -1 : 1;
        return a.name.localeCompare(b.name, 'fr');
      });

      setPractitioners(sorted.slice(0, 8));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Erreur de chargement des praticiens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatured();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const premium = practitioners.filter((p) => p.subscriptionTier === 'premium');
  const standard = practitioners.filter((p) => p.subscriptionTier !== 'premium');

  return (
    <section id="a-la-une" className="py-20 md:py-28 bg-background-100">
      <div className="px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-700 mb-3">
              À la une
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight mb-4">
              Nos praticiens <span className="italic text-accent-800">de santé.</span>
            </h2>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              Une sélection de professionnels de santé de qualité.
              Disponibilités à jour en temps réel.
            </p>
          </div>
          <Link
            to="/recherche"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            Voir tous les praticiens
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
            <p className="text-sm text-foreground-600 mt-4">Chargement des praticiens…</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-5">
              <i className="ri-error-warning-line text-3xl text-accent-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Impossible de charger les praticiens
            </h3>
            <p className="text-sm text-foreground-600 max-w-md mx-auto mb-6 leading-relaxed">
              {loadError}
            </p>
            <button
              onClick={fetchFeatured}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
              Réessayer
            </button>
          </div>
        ) : practitioners.length > 0 ? (
          <div className="space-y-10">
            {premium.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {premium.map((p) => (
                  <FeatureCard key={p.id} practitioner={p} large />
                ))}
              </div>
            )}
            {standard.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {standard.map((p) => (
                  <FeatureCard key={p.id} practitioner={p} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-5">
              <i className="ri-time-line text-3xl text-secondary-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Aucun praticien à la une pour l'instant
            </h3>
            <p className="text-sm text-foreground-600 max-w-md mx-auto">
              Les praticiens mis en avant apparaîtront ici.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}